import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, RefreshCw, Loader2, CheckCircle2, AlertTriangle, UserX, Clock, Copy, Check, Inbox, KeyRound, Save, ShieldCheck, Send, MessageSquareText, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

import { useBrandName } from '@/lib/branding';
const STATUS_META = {
  received: { label: 'received', icon: Clock, color: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
  answered: { label: 'answered', icon: CheckCircle2, color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  ai_failed: { label: 'AI failed', icon: AlertTriangle, color: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  unknown_sender: { label: 'unknown sender', icon: UserX, color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  blocked_domain: { label: 'blocked domain', icon: Ban, color: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  ignored: { label: 'ignored', icon: Clock, color: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.received;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium ${meta.color}`} data-testid={`inbound-status-${status}`}>
      <Icon className="w-3 h-3" />{meta.label}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function InboundEmailsPage() {
  const APP = useBrandName();
  const [emails, setEmails] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [copied, setCopied] = useState(false);
  const [resendSecret, setResendSecret] = useState('');
  const [sendgridSecret, setSendgridSecret] = useState('');
  const [savingSecrets, setSavingSecrets] = useState(false);
  const [inboundAddress, setInboundAddress] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  const [addrCopied, setAddrCopied] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState('');
  const [replySignature, setReplySignature] = useState('');
  const [replyAppUrl, setReplyAppUrl] = useState('');
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const [list, cfg] = await Promise.all([
        api.listInboundEmails(params.toString()),
        api.getInboundEmailConfig().catch(() => null),
      ]);
      setEmails(Array.isArray(list) ? list : []);
      setConfig(cfg);
      setInboundAddress(((cfg && cfg.inbound_email_address) || '').trim());
      setAllowedDomains(((cfg && cfg.allowed_domains) || []).join(', '));
      setReplySignature(((cfg && cfg.reply_signature) || '').trim());
      setReplyAppUrl(((cfg && cfg.reply_app_url) || '').trim());
    } catch (err) {
      toast.error(err.message || 'Could not load inbound emails');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const counts = emails.reduce((acc, e) => { acc[e.status] = (acc[e.status] || 0) + 1; return acc; }, {});

  const copyWebhook = async () => {
    if (!config?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(config.webhook_url);
      setCopied(true);
      toast.success('Webhook URL copied');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy — select the URL manually');
    }
  };

  const effectiveAddress = (config?.inbound_email_address || '').trim();
  const suggestedAddress = (config?.suggested_inbound_address || '').trim();

  const copyAddress = async () => {
    if (!effectiveAddress) return;
    try {
      await navigator.clipboard.writeText(effectiveAddress);
      setAddrCopied(true);
      toast.success('Email address copied');
      setTimeout(() => setAddrCopied(false), 2500);
    } catch {
      toast.error('Could not copy — select the address manually');
    }
  };

  const saveAddress = async () => {
    const addr = inboundAddress.trim().toLowerCase();
    setSavingAddress(true);
    try {
      await api.updateInboundEmailConfig({ inbound_email_address: addr });
      toast.success(addr ? 'Inbound email address saved' : 'Inbound email address cleared');
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not save address');
    } finally {
      setSavingAddress(false);
    }
  };

  const savePrefs = async () => {
    const domains = allowedDomains.split(/[\s,;]+/).map(d => d.trim().toLowerCase().replace(/^@/, '')).filter(Boolean);
    setSavingPrefs(true);
    try {
      await api.updateInboundEmailConfig({
        allowed_domains: domains,
        reply_signature: replySignature.trim(),
        reply_app_url: replyAppUrl.trim(),
      });
      toast.success('Inbound preferences saved');
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const res = await api.sendTestInboundEmail();
      if (res.status === 'answered') {
        toast.success(`Test sent — check ${res.sent_to || 'your inbox'} for the reply`);
      } else if (res.status === 'ai_failed') {
        toast.error('The AI could not answer the test — check the inbox row for details');
      } else if (res.status === 'blocked_domain') {
        toast.error('Blocked by your own sender allowlist — add your domain to test');
      } else {
        toast.message(`Test result: ${res.status}`);
      }
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not send test email');
    } finally {
      setSendingTest(false);
    }
  };


  const saveSecrets = async () => {    const payload = {};
    if (resendSecret.trim()) payload.resend_webhook_secret = resendSecret.trim();
    if (sendgridSecret.trim()) payload.inbound_email_secret = sendgridSecret.trim();
    if (Object.keys(payload).length === 0) {
      toast.error('Enter at least one secret to save');
      return;
    }
    setSavingSecrets(true);
    try {
      await api.updateInboundEmailConfig(payload);
      toast.success('Webhook secrets saved for your workspace');
      setResendSecret('');
      setSendgridSecret('');
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not save secrets');
    } finally {
      setSavingSecrets(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="inbound-emails-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Inbox className="w-6 h-6 text-primary" /> Email-to-Insight
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Customers email a plain-English question and {APP} replies with the answer. Recent inbound questions and statuses appear here.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="rounded-lg" data-testid="refresh-inbound-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Where customers send emails — the primary thing users need to know */}
      <div className="glass-card rounded-2xl p-5 border border-primary/20" data-testid="inbound-address-card">
        <p className="text-xs font-heading font-semibold text-foreground mb-1 flex items-center gap-2">
          <Mail className="w-4 h-4 text-primary" /> Where customers send emails
        </p>
        <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
          Share this address with your team and customers. They email a plain-English question to it, and {APP} replies with the answer. This must be an address you&apos;ve set up to receive mail (in Resend or SendGrid) and route to the webhook below.
        </p>

        {effectiveAddress ? (
          <div className="flex items-center gap-2" data-testid="inbound-address-display">
            <code className="flex-1 min-w-0 text-sm font-mono text-foreground bg-background/60 border border-border/30 rounded-lg px-3 py-2.5 truncate">
              {effectiveAddress}
            </code>
            <Button size="sm" variant="outline" onClick={copyAddress} className="rounded-lg h-9 px-3 flex-shrink-0" data-testid="copy-inbound-address-btn">
              {addrCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
        ) : (
          <p className="text-[11px] text-amber-400 bg-amber-500/5 border border-amber-500/30 rounded-lg px-3 py-2 mb-1" data-testid="inbound-address-empty">
            No inbound address set yet — customers don&apos;t know where to write. Set it below.
          </p>
        )}

        <div className="mt-3 pt-3 border-t border-border/30">
          <label className="text-[10px] text-muted-foreground uppercase tracking-wide block mb-1">Set / change the inbound email address</label>
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={inboundAddress}
              onChange={(e) => setInboundAddress(e.target.value)}
              placeholder={suggestedAddress || 'ask@yourcompany.com'}
              autoComplete="off"
              className="flex-1 min-w-0 text-[13px] font-mono text-foreground bg-background/60 border border-border/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/40"
              data-testid="inbound-address-input"
            />
            <Button size="sm" onClick={saveAddress} disabled={savingAddress} className="rounded-lg h-9 px-4 flex-shrink-0" data-testid="save-inbound-address-btn">
              {savingAddress ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
              Save
            </Button>
          </div>
          {suggestedAddress && !inboundAddress.trim() && (
            <button
              type="button"
              onClick={() => setInboundAddress(suggestedAddress)}
              className="mt-2 text-[10px] text-primary hover:underline"
              data-testid="use-suggested-address-btn"
            >
              Use suggested: {suggestedAddress}
            </button>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-border/30 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[10px] text-muted-foreground">
            Confirm the whole loop works before sharing — we&apos;ll email a sample question&apos;s answer to your own address.
          </p>
          <Button size="sm" variant="outline" onClick={sendTest} disabled={sendingTest} className="rounded-lg h-8 px-4 flex-shrink-0" data-testid="send-test-email-btn">
            {sendingTest ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-2" />}
            Send myself a test
          </Button>
        </div>
      </div>

      {/* Sender allowlist + reply branding */}
      {config && (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Sender allowlist */}
          <div className="glass-card rounded-2xl p-5" data-testid="inbound-allowlist-card">
            <p className="text-xs font-heading font-semibold text-foreground mb-1 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary" /> Sender allowlist
            </p>
            <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
              Only accept questions from these email domains, so strangers can&apos;t probe your data. Leave blank to allow any registered user. One domain per line or comma-separated (e.g. <span className="font-mono">yourcompany.com</span>).
            </p>
            <textarea
              value={allowedDomains}
              onChange={(e) => setAllowedDomains(e.target.value)}
              placeholder={'yourcompany.com\npartner.com'}
              rows={3}
              className="w-full text-[12px] font-mono text-foreground bg-background/60 border border-border/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/40 resize-none"
              data-testid="inbound-allowlist-input"
            />
          </div>

          {/* Reply branding */}
          <div className="glass-card rounded-2xl p-5" data-testid="inbound-branding-card">
            <p className="text-xs font-heading font-semibold text-foreground mb-1 flex items-center gap-2">
              <MessageSquareText className="w-4 h-4 text-primary" /> Reply branding
            </p>
            <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
              Customise the footer of every AI email reply — your sign-off line and an &quot;open the app&quot; link.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wide block mb-1">Sign-off line</label>
                <input
                  type="text"
                  value={replySignature}
                  onChange={(e) => setReplySignature(e.target.value)}
                  placeholder={`Answered by ${APP}'s ambient assistant. Reply to ask another question.`}
                  className="w-full text-[12px] text-foreground bg-background/60 border border-border/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/40"
                  data-testid="inbound-signature-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wide block mb-1">&quot;Open the app&quot; link</label>
                <input
                  type="url"
                  value={replyAppUrl}
                  onChange={(e) => setReplyAppUrl(e.target.value)}
                  placeholder="https://app.yourcompany.com"
                  className="w-full text-[12px] font-mono text-foreground bg-background/60 border border-border/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/40"
                  data-testid="inbound-appurl-input"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 -mt-1">
            <Button size="sm" onClick={savePrefs} disabled={savingPrefs} className="rounded-lg h-8 px-4" data-testid="save-inbound-prefs-btn">
              {savingPrefs ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
              Save allowlist &amp; branding
            </Button>
          </div>
        </div>
      )}

      {/* Setup card */}
      {config && (
        <div className="glass-card rounded-2xl p-5" data-testid="inbound-setup-card">
          <p className="text-xs font-heading font-semibold text-foreground mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" /> Inbound email webhook (Resend)
          </p>
          <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
            In Resend: verify your domain and enable <span className="font-semibold">Receiving</span> (resend.com/domains), then add a webhook for the <span className="font-mono">email.received</span> event pointing to the URL below. Add your workspace&apos;s signing secret (whsec_…) in the form below to enable signature verification — no environment variable or redeploy needed.
            Legacy SendGrid Inbound Parse is still supported at <span className="font-mono">/api/inbound/email</span> — it authenticates via the
            <code className="px-1 bg-muted/40 rounded mx-1">X-Inbound-Secret</code> header matching the secret you save below.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 text-[11px] font-mono text-foreground bg-background/60 border border-border/30 rounded-lg px-3 py-2 truncate" data-testid="inbound-webhook-url">
              {config.webhook_url || '— set PUBLIC_BACKEND_URL env var to surface the full URL —'}
            </code>
            <Button size="sm" variant="outline" onClick={copyWebhook} className="rounded-lg h-8 px-3 flex-shrink-0" data-testid="copy-webhook-btn">
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px]">
            <span className={`px-2 py-0.5 rounded-md border ${config.secret_configured ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 'border-amber-500/30 text-amber-400 bg-amber-500/5'}`} data-testid="inbound-secret-status">
              {config.secret_configured
                ? `Resend secret configured${config.tenant_resend_secret_set ? ' (this workspace)' : ' (system default)'}`
                : 'Resend secret not set — webhook is unauthenticated'}
            </span>
            <span className={`px-2 py-0.5 rounded-md border ${config.sendgrid_secret_configured ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 'border-slate-500/30 text-slate-400 bg-slate-500/5'}`} data-testid="inbound-sendgrid-secret-status">
              {config.sendgrid_secret_configured
                ? `SendGrid secret configured${config.tenant_inbound_secret_set ? ' (this workspace)' : ' (system default)'}`
                : 'SendGrid secret not set'}
            </span>
            <span className={`px-2 py-0.5 rounded-md border ${config.email_configured ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 'border-amber-500/30 text-amber-400 bg-amber-500/5'}`} data-testid="inbound-email-status">
              {config.email_configured ? 'Outbound email configured' : 'Outbound email not configured — replies will be skipped'}
            </span>
          </div>

          {/* Per-workspace secret configuration */}
          <div className="mt-4 pt-4 border-t border-border/30" data-testid="inbound-secret-form">
            <p className="text-[11px] font-heading font-semibold text-foreground mb-1 flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5 text-primary" /> Webhook secrets for this workspace
            </p>
            <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
              Use your own Resend domain by saving your workspace&apos;s signing secret here — it overrides the system-wide default. Secrets are stored securely and never shown again. Leave a field blank to keep the current value; clear a saved secret to fall back to the default.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wide block mb-1">Resend signing secret (whsec_…)</label>
                <input
                  type="password"
                  value={resendSecret}
                  onChange={(e) => setResendSecret(e.target.value)}
                  placeholder={config.tenant_resend_secret_set ? '•••••••• (saved — enter to replace)' : 'whsec_…'}
                  autoComplete="off"
                  className="w-full text-[11px] font-mono text-foreground bg-background/60 border border-border/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/40"
                  data-testid="inbound-resend-secret-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wide block mb-1">SendGrid X-Inbound-Secret</label>
                <input
                  type="password"
                  value={sendgridSecret}
                  onChange={(e) => setSendgridSecret(e.target.value)}
                  placeholder={config.tenant_inbound_secret_set ? '•••••••• (saved — enter to replace)' : 'optional'}
                  autoComplete="off"
                  className="w-full text-[11px] font-mono text-foreground bg-background/60 border border-border/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/40"
                  data-testid="inbound-sendgrid-secret-input"
                />
              </div>
            </div>
            <div className="mt-3">
              <Button size="sm" onClick={saveSecrets} disabled={savingSecrets} className="rounded-lg h-8 px-4" data-testid="inbound-save-secrets-btn">
                {savingSecrets ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
                Save secrets
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Status counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {['received', 'answered', 'ai_failed', 'unknown_sender'].map(s => (
          <div key={s} className="glass-card rounded-2xl p-4" data-testid={`inbound-count-${s}`}>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{STATUS_META[s].label}</p>
            <p className="text-2xl font-heading font-bold text-foreground mt-1">{counts[s] || 0}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">Status</span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-8 rounded-lg text-xs" data-testid="inbound-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="answered">Answered</SelectItem>
            <SelectItem value="ai_failed">AI failed</SelectItem>
            <SelectItem value="unknown_sender">Unknown sender</SelectItem>
            <SelectItem value="blocked_domain">Blocked domain</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Emails list */}
      <div className="glass-card rounded-2xl overflow-hidden">
        {loading && emails.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : emails.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm" data-testid="inbound-empty-state">
            No inbound emails yet. Once the inbound webhook is set up, customer questions will appear here.
          </div>
        ) : (
          <div className="divide-y divide-border/20">
            {emails.map(e => {
              const key = e.log_id || `${e.from}-${e.received_at}`;
              const isExpanded = expanded[key];
              return (
                <div key={key} className="p-4 hover:bg-muted/10 cursor-pointer" onClick={() => setExpanded(x => ({ ...x, [key]: !isExpanded }))} data-testid={`inbound-row-${key}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-mono text-foreground/90 truncate">{e.from || '(no sender)'}</span>
                        <StatusBadge status={e.status} />
                        <span className="text-[10px] text-muted-foreground/70">{fmtTime(e.received_at)}</span>
                      </div>
                      <p className="text-xs text-foreground truncate">{e.subject || <em className="text-muted-foreground">(no subject)</em>}</p>
                      {!isExpanded && e.question && (
                        <p className="text-[11px] text-muted-foreground/80 truncate mt-0.5">{e.question}</p>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="mt-3 grid grid-cols-1 gap-3 text-[11px]" data-testid={`inbound-details-${key}`}>
                      {e.question && (
                        <div>
                          <p className="text-muted-foreground mb-1">Question</p>
                          <pre className="font-mono text-foreground/85 bg-muted/20 border border-border/30 rounded-lg p-2 whitespace-pre-wrap break-words">{e.question}</pre>
                        </div>
                      )}
                      {e.error && (
                        <div>
                          <p className="text-rose-400 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Error</p>
                          <pre className="font-mono text-rose-300/80 bg-rose-500/5 border border-rose-500/20 rounded-lg p-2 whitespace-pre-wrap break-words">{e.error}</pre>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                        <span>To: <span className="text-foreground/80 font-mono">{e.to || '—'}</span></span>
                        {e.answered_at && <span>Answered: <span className="text-foreground/80">{fmtTime(e.answered_at)}</span></span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
